import { mutationOptions, UseMutationOptions } from '@tanstack/react-query';
import { ApiResponse, BaseApiResponse, HttpResponse } from './Api';
import type { ApiClient } from './runtime/apiRuntime';
import { apiOf } from './runtime/binding';
import { unwrapApiResponse } from './unwrap';

type HttpApiResponse<T> = Promise<HttpResponse<ApiResponse<T>, BaseApiResponse>>;

/**
 * Mutation options for an endpoint. `select` picks the endpoint from the Api of the QueryClient that
 * runs the mutation (the endpoints are arrow functions, so a picked one stays bound to that Api).
 */
export function apiMutationOptions<TData, TVariables, TOnMutateResult>(
    select: (api: ApiClient) => (variables: TVariables) => HttpApiResponse<TData>,
    options?: Partial<UseMutationOptions<TData, BaseApiResponse, TVariables, TOnMutateResult>>
) {
    return mutationOptions<TData, BaseApiResponse, TVariables, TOnMutateResult>({
        mutationFn: (variables, context) => unwrapApiResponse(select(apiOf(context.client))(variables)),
        ...options
    });
}

export type BaseApiMutationOptions<T extends (...args: any) => HttpApiResponse<any>> = Partial<
    UseMutationOptions<
        T extends (...args: any) => HttpApiResponse<infer U> ? U : any,
        BaseApiResponse,
        Parameters<T>[0] extends undefined ? void : Parameters<T>[0],
        any
    >
>;
